/**
 * CommunicationPlanPanel — table editor for the Communication Plan
 * artifact (stakeholder × deliverable rows with frequency, priority,
 * owner, delivery method, comments).
 *
 * Pass `scope="project"` and the matching entity id. The panel handles its own loading/error/empty states and
 * inline-edit affordances.
 *
 * Editing model: click any cell to edit it inline. Required fields
 * (stakeholder + deliverable) prevent saving when blank. Enum-ish
 * fields (frequency / priority / delivery method) render as <select>
 * with a "— None —" option and a final "Custom…" entry that flips the
 * cell to a text input so the user can override.
 */
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertCircle,
  Loader2,
  MessageSquare,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/cn";
import {
  useCommunicationPlan,
  useCreateCommPlanEntry,
  useUpdateCommPlanEntry,
  useDeleteCommPlanEntry,
  useCommunicationRoster,
  useCreateRosterEntry,
  useUpdateRosterEntry,
  useDeleteRosterEntry,
  FREQUENCY_OPTIONS,
  PRIORITY_OPTIONS,
  DELIVERY_METHOD_OPTIONS,
  type CommPlanEntry,
  type CommPlanScope,
  type RosterEntry,
} from "@/hooks/useCommunicationPlan";
import { Users } from "lucide-react";

interface Props {
  scope: CommPlanScope;
  scopeId: string;
}

const PRIORITY_TONE: Record<string, string> = {
  High: "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 ring-1 ring-rose-200 dark:ring-rose-500/30",
  Medium: "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-500/30",
  Low: "bg-raised text-ink-3 ring-1 ring-line",
};

const ROW_GRID =
  "grid grid-cols-[minmax(140px,1fr)_minmax(180px,1.5fr)_120px_100px_140px_140px_minmax(180px,1.5fr)_32px] items-start gap-3";

export function CommunicationPlanPanel({ scope, scopeId }: Props) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="space-y-6"
    >
      <RosterSection scope={scope} scopeId={scopeId} />
      <PlanSection scope={scope} scopeId={scopeId} />
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Plan section — original stakeholder × deliverable matrix
// ---------------------------------------------------------------------------

function PlanSection({ scope, scopeId }: Props) {
  const list = useCommunicationPlan(scope, scopeId);
  const create = useCreateCommPlanEntry(scope, scopeId);
  const update = useUpdateCommPlanEntry(scope, scopeId);
  const del = useDeleteCommPlanEntry(scope, scopeId);

  const handleAdd = () => {
    create.mutate(
      { stakeholder: "New stakeholder", deliverable: "New deliverable" },
      { onError: (err) => toast.error(`Add failed: ${err.message}`) },
    );
  };

  const handleUpdate = (id: number, patch: Partial<CommPlanEntry>) => {
    update.mutate(
      { id, patch },
      { onError: (err) => toast.error(`Save failed: ${err.message}`) },
    );
  };

  const handleDelete = (id: number) => {
    if (!confirm("Delete this row?")) return;
    del.mutate(id, { onError: (err) => toast.error(`Delete failed: ${err.message}`) });
  };

  const entries = list.data ?? [];

  return (
    <div className="rounded-xl bg-surface ring-1 ring-line">
      <div className="flex items-center justify-between border-b border-line-soft px-5 py-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-brand-navy-500" />
          <h2 className="text-sm font-semibold text-ink">Communication Plan</h2>
          <span className="rounded-full bg-raised px-2 py-0.5 text-xs font-medium tabular-nums text-ink-3">
            {entries.length} {entries.length === 1 ? "row" : "rows"}
          </span>
        </div>
        <button
          onClick={handleAdd}
          disabled={create.isPending}
          className="inline-flex items-center gap-1 rounded-lg bg-brand-navy-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-brand-navy-700 disabled:opacity-50"
        >
          {create.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Plus className="h-3.5 w-3.5" />
          )}
          Add row
        </button>
      </div>

      <div className="overflow-x-auto p-4">
        {list.isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-ink-subtle" />
          </div>
        ) : list.isError ? (
          <ErrorState label="Couldn't load communication plan." err={list.error as Error} />
        ) : entries.length === 0 ? (
          <EmptyPlanState onAdd={handleAdd} adding={create.isPending} />
        ) : (
          <div className="min-w-[1100px]">
            <PlanTableHeader />
            <ul className="mt-1 space-y-1">
              {entries.map((e) => (
                <PlanRow
                  key={e.id}
                  entry={e}
                  onSave={(patch) => handleUpdate(e.id, patch)}
                  onDelete={() => handleDelete(e.id)}
                />
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Roster section — Name · Project Role · Email · Phone
// ---------------------------------------------------------------------------

const ROSTER_GRID =
  "grid grid-cols-[minmax(160px,1.2fr)_minmax(140px,1fr)_minmax(180px,1.5fr)_140px_32px] items-start gap-3";

function RosterSection({ scope, scopeId }: Props) {
  const list = useCommunicationRoster(scope, scopeId);
  const create = useCreateRosterEntry(scope, scopeId);
  const update = useUpdateRosterEntry(scope, scopeId);
  const del = useDeleteRosterEntry(scope, scopeId);

  const handleAdd = () => {
    create.mutate(
      { name: "New person" },
      { onError: (err) => toast.error(`Add failed: ${err.message}`) },
    );
  };

  const handleUpdate = (id: number, patch: Partial<RosterEntry>) => {
    update.mutate(
      { id, patch },
      { onError: (err) => toast.error(`Save failed: ${err.message}`) },
    );
  };

  const handleDelete = (id: number) => {
    if (!confirm("Delete this person?")) return;
    del.mutate(id, { onError: (err) => toast.error(`Delete failed: ${err.message}`) });
  };

  const entries = list.data ?? [];

  return (
    <div className="rounded-xl bg-surface ring-1 ring-line">
      <div className="flex items-center justify-between border-b border-line-soft px-5 py-3">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-brand-navy-500" />
          <h2 className="text-sm font-semibold text-ink">Roster</h2>
          <span className="rounded-full bg-raised px-2 py-0.5 text-xs font-medium tabular-nums text-ink-3">
            {entries.length} {entries.length === 1 ? "person" : "people"}
          </span>
        </div>
        <button
          onClick={handleAdd}
          disabled={create.isPending}
          className="inline-flex items-center gap-1 rounded-lg bg-brand-navy-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-brand-navy-700 disabled:opacity-50"
        >
          {create.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Plus className="h-3.5 w-3.5" />
          )}
          Add person
        </button>
      </div>

      <div className="overflow-x-auto p-4">
        {list.isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-ink-subtle" />
          </div>
        ) : list.isError ? (
          <ErrorState label="Couldn't load roster." err={list.error as Error} />
        ) : entries.length === 0 ? (
          <EmptyRosterState onAdd={handleAdd} adding={create.isPending} />
        ) : (
          <div className="min-w-[700px]">
            <RosterTableHeader />
            <ul className="mt-1 space-y-1">
              {entries.map((e) => (
                <RosterRow
                  key={e.id}
                  entry={e}
                  onSave={(patch) => handleUpdate(e.id, patch)}
                  onDelete={() => handleDelete(e.id)}
                />
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function RosterTableHeader() {
  return (
    <div
      className={cn(
        ROSTER_GRID,
        "border-b border-line px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-muted",
      )}
    >
      <span>Name</span>
      <span>Project Role</span>
      <span>Email</span>
      <span>Phone</span>
      <span aria-hidden />
    </div>
  );
}

function RosterRow({
  entry,
  onSave,
  onDelete,
}: {
  entry: RosterEntry;
  onSave: (patch: Partial<RosterEntry>) => void;
  onDelete: () => void;
}) {
  return (
    <li
      className={cn(
        ROSTER_GRID,
        "rounded-md border border-transparent px-2 py-1.5 text-sm hover:border-line hover:bg-raised/60",
      )}
    >
      <TextCell value={entry.name} onSave={(v) => onSave({ name: v ?? "" })} required />
      <TextCell value={entry.project_role} onSave={(v) => onSave({ project_role: v })} placeholder="—" />
      <TextCell value={entry.email} onSave={(v) => onSave({ email: v })} placeholder="—" />
      <TextCell value={entry.phone} onSave={(v) => onSave({ phone: v })} placeholder="—" />
      <button
        onClick={onDelete}
        className="self-center rounded p-1 text-ink-faint hover:bg-rose-50 dark:hover:bg-rose-500/15 hover:text-rose-600 dark:hover:text-rose-400"
        title="Delete person"
        aria-label="Delete person"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}

function EmptyRosterState({ onAdd, adding }: { onAdd: () => void; adding: boolean }) {
  return (
    <div className="rounded-lg border border-dashed border-line bg-canvas p-6 text-center">
      <Users className="mx-auto h-5 w-5 text-ink-faint" />
      <p className="mt-2 text-sm text-ink-3">No roster entries yet.</p>
      <p className="mt-1 text-xs text-ink-muted">
        List the people on this project — name, role, email, phone.
      </p>
      <button
        onClick={onAdd}
        disabled={adding}
        className="mt-3 inline-flex items-center gap-1 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-navy-700 disabled:opacity-50"
      >
        {adding ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Plus className="h-3.5 w-3.5" />
        )}
        Add first person
      </button>
    </div>
  );
}

function ErrorState({ label, err }: { label: string; err: Error }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/10 p-3 text-sm text-rose-700 dark:text-rose-300">
      <AlertCircle className="mt-0.5 h-4 w-4" />
      <div>
        <div className="font-medium">{label}</div>
        <div className="mt-0.5 text-xs">{err.message}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function PlanTableHeader() {
  return (
    <div
      className={cn(
        ROW_GRID,
        "border-b border-line px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-muted",
      )}
    >
      <span>Stakeholder</span>
      <span>Deliverable</span>
      <span>Frequency</span>
      <span>Priority</span>
      <span>Owner</span>
      <span>Delivery method</span>
      <span>Comments</span>
      <span aria-hidden />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface RowProps {
  entry: CommPlanEntry;
  onSave: (patch: Partial<CommPlanEntry>) => void;
  onDelete: () => void;
}

function PlanRow({ entry, onSave, onDelete }: RowProps) {
  return (
    <li
      className={cn(
        ROW_GRID,
        "rounded-md border border-transparent px-2 py-1.5 text-sm hover:border-line hover:bg-raised/60",
      )}
    >
      <TextCell
        value={entry.stakeholder}
        onSave={(v) => onSave({ stakeholder: v })}
        required
      />
      <TextCell
        value={entry.deliverable}
        onSave={(v) => onSave({ deliverable: v })}
        required
      />
      <EnumCell
        value={entry.frequency}
        options={FREQUENCY_OPTIONS}
        onSave={(v) => onSave({ frequency: v })}
      />
      <EnumCell
        value={entry.priority}
        options={PRIORITY_OPTIONS}
        renderPill={(v) =>
          v && PRIORITY_TONE[v] ? (
            <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", PRIORITY_TONE[v])}>
              {v}
            </span>
          ) : null
        }
        onSave={(v) => onSave({ priority: v })}
      />
      <TextCell
        value={entry.owner}
        onSave={(v) => onSave({ owner: v })}
        placeholder="Unassigned"
      />
      <EnumCell
        value={entry.delivery_method}
        options={DELIVERY_METHOD_OPTIONS}
        onSave={(v) => onSave({ delivery_method: v })}
      />
      <TextCell
        value={entry.comments}
        onSave={(v) => onSave({ comments: v })}
        multiline
        placeholder="—"
      />
      <button
        onClick={onDelete}
        className="self-center rounded p-1 text-ink-faint hover:bg-rose-50 dark:hover:bg-rose-500/15 hover:text-rose-600 dark:hover:text-rose-400"
        title="Delete row"
        aria-label="Delete row"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

function TextCell({
  value,
  onSave,
  required = false,
  multiline = false,
  placeholder,
}: {
  value: string | null | undefined;
  onSave: (v: string | null) => void;
  required?: boolean;
  multiline?: boolean;
  placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");

  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);

  const commit = () => {
    const trimmed = draft.trim();
    if (required && !trimmed) {
      toast.error("Required field can't be blank.");
      setDraft(value ?? "");
      setEditing(false);
      return;
    }
    if ((trimmed || "") !== (value || "")) {
      onSave(trimmed || (required ? trimmed : null));
    }
    setEditing(false);
  };

  if (editing) {
    if (multiline) {
      return (
        <textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          rows={2}
          className="w-full rounded border border-brand-navy-300 dark:border-brand-navy-500/40 bg-surface px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-navy-200"
        />
      );
    }
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") {
            setDraft(value ?? "");
            setEditing(false);
          }
        }}
        className="w-full rounded border border-brand-navy-300 dark:border-brand-navy-500/40 bg-surface px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-navy-200"
      />
    );
  }

  const display = (value || "").trim();
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={cn(
        "block w-full truncate rounded px-2 py-1 text-left text-sm text-ink hover:bg-surface hover:ring-1 hover:ring-line",
        !display && "italic text-ink-subtle",
        multiline && "whitespace-pre-wrap break-words",
      )}
      title={display}
    >
      {display || placeholder || "—"}
    </button>
  );
}

function EnumCell({
  value,
  options,
  onSave,
  renderPill,
}: {
  value: string | null | undefined;
  options: readonly string[];
  onSave: (v: string | null) => void;
  renderPill?: (v: string | null) => React.ReactNode;
}) {
  // Editing modes: 'closed' | 'select' | 'custom'
  const [mode, setMode] = useState<"closed" | "select" | "custom">("closed");
  const [customDraft, setCustomDraft] = useState("");

  // The display value lives on the entry itself; if it doesn't match
  // any preset, that's a "Custom…" override the user previously saved.
  const isPreset = useMemo(
    () => Boolean(value && options.includes(value)),
    [value, options],
  );

  if (mode === "select") {
    return (
      <select
        autoFocus
        value={value ?? ""}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "__custom__") {
            setCustomDraft(value && !isPreset ? value : "");
            setMode("custom");
            return;
          }
          onSave(v || null);
          setMode("closed");
        }}
        onBlur={() => setMode("closed")}
        className="w-full rounded border border-brand-navy-300 dark:border-brand-navy-500/40 bg-surface px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-navy-200"
      >
        <option value="">— None —</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
        {value && !isPreset && (
          <option value={value}>{value} (custom)</option>
        )}
        <option value="__custom__">Custom…</option>
      </select>
    );
  }

  if (mode === "custom") {
    return (
      <input
        autoFocus
        value={customDraft}
        onChange={(e) => setCustomDraft(e.target.value)}
        onBlur={() => {
          const v = customDraft.trim();
          if ((v || "") !== (value || "")) onSave(v || null);
          setMode("closed");
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            const v = customDraft.trim();
            if ((v || "") !== (value || "")) onSave(v || null);
            setMode("closed");
          }
          if (e.key === "Escape") setMode("closed");
        }}
        placeholder="Custom value…"
        className="w-full rounded border border-brand-navy-300 dark:border-brand-navy-500/40 bg-surface px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-navy-200"
      />
    );
  }

  const display = (value || "").trim();
  const pill = renderPill ? renderPill(value ?? null) : null;
  return (
    <button
      type="button"
      onClick={() => setMode("select")}
      className={cn(
        "block w-full truncate rounded px-2 py-1 text-left text-sm text-ink hover:bg-surface hover:ring-1 hover:ring-line",
        !display && "italic text-ink-subtle",
      )}
    >
      {pill ?? (display || "—")}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyPlanState({ onAdd, adding }: { onAdd: () => void; adding: boolean }) {
  return (
    <div className="rounded-lg border border-dashed border-line bg-canvas p-8 text-center">
      <MessageSquare className="mx-auto h-6 w-6 text-ink-faint" />
      <p className="mt-2 text-sm text-ink-3">No communication plan rows yet.</p>
      <p className="mt-1 text-xs text-ink-muted">
        Add one row per stakeholder × deliverable combo. Frequency, priority,
        owner, and delivery method are optional.
      </p>
      <button
        onClick={onAdd}
        disabled={adding}
        className="mt-3 inline-flex items-center gap-1 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-navy-700 disabled:opacity-50"
      >
        {adding ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Plus className="h-3.5 w-3.5" />
        )}
        Add first row
      </button>
    </div>
  );
}
